import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  Sd844TemplateColumnMapping,
  Sd844TemplateSampleSource,
} from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { useDebounce } from '@/hooks/useDebounce';

/**
 * Per-supplier Excel templates for 844 email claims.
 *
 * A supplier with an enabled template gets THEIR form, populated, instead of
 * the enabled organization default or built-in workbook.
 */

/** Every S&D supplier and whether its 844 uses a custom template. */
export function useSd844TemplateSuppliers() {
  return useQuery(
    orpc.shipAndDebit.sd844EmailTemplates.listSuppliers.queryOptions(),
  );
}

/** A save or delete changes both the template and the supplier list's badge. */
function invalidateTemplates(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.sd844EmailTemplates.get.key(),
    }),
    queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.sd844EmailTemplates.listSuppliers.key(),
    }),
  ]);
}

/**
 * A supplier's own template (or, `companyId: null`, the org default), or
 * `null` when there is none.
 */
export function useSd844EmailTemplate(
  companyId: string | null,
  enabled = true,
) {
  return useQuery(
    orpc.shipAndDebit.sd844EmailTemplates.get.queryOptions({
      input: enabled ? { companyId } : skipToken,
    }),
  );
}

/** Save the built-in default as the org's editable default template. */
export function useCreateSd844DefaultFromBuiltIn() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.shipAndDebit.sd844EmailTemplates.createDefaultFromBuiltIn.mutationOptions(),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}

/** The built-in default workbook, empty — to edit and upload as the org default. */
export function useDownloadBuiltInSd844Default() {
  return useMutation(
    orpc.shipAndDebit.sd844EmailTemplates.downloadBuiltInDefault.mutationOptions(),
  );
}

/** Inspect an uploaded workbook so the mapping editor can offer its columns. */
export function useParseSd844TemplateUpload() {
  return useMutation(
    orpc.shipAndDebit.sd844EmailTemplates.parseUpload.mutationOptions(),
  );
}

/**
 * Propose a source for each column of a freshly uploaded workbook.
 *
 * A mutation rather than a query: it is a one-shot action tied to an upload,
 * and nothing should re-run it in the background.
 */
export function useSuggestSd844TemplateMappings() {
  return useMutation(
    orpc.shipAndDebit.sd844EmailTemplates.suggestMappings.mutationOptions(),
  );
}

export function useSaveSd844EmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.shipAndDebit.sd844EmailTemplates.save.mutationOptions(),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}

export function useSetSd844EmailTemplateEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.shipAndDebit.sd844EmailTemplates.setEnabled.mutationOptions(),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}

export function useDeleteSd844EmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.shipAndDebit.sd844EmailTemplates.delete.mutationOptions(),
    onSuccess: () => invalidateTemplates(queryClient),
  });
}

/**
 * Render the template against real sent lines and hand back the bytes.
 *
 * A mutation rather than a query: it is an explicit "show me the file" action,
 * and nothing should re-render it in the background.
 */
export function usePreviewSd844EmailTemplate() {
  return useMutation(
    orpc.shipAndDebit.sd844EmailTemplates.preview.mutationOptions(),
  );
}

/**
 * The value every binding would write for ONE real line, refreshed as the
 * bindings change.
 *
 * Debounced so picking through a dropdown is one request, not one per option.
 * Values are hidden while the bindings are settling so a previous binding's
 * value is never shown beside the current selection.
 */
export function useSd844TemplateSampleValues({
  companyId,
  sample,
  columnMappings,
  enabled,
}: {
  companyId: string | null;
  sample: Sd844TemplateSampleSource;
  columnMappings: Sd844TemplateColumnMapping[];
  enabled: boolean;
}) {
  const debounced = useDebounce(columnMappings, 600);
  const query = useQuery({
    ...orpc.shipAndDebit.sd844EmailTemplates.sampleValues.queryOptions({
      input:
        enabled && debounced.length > 0
          ? { companyId, sample, columnMappings: debounced }
          : skipToken,
    }),
  });

  if (debounced === columnMappings) return query;
  return { ...query, data: undefined, isFetching: true };
}
